import {useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiBookOpen,
  FiCalculator,
  FiFileText,
  FiTag
}=FiIcons;

const practiceTools=[
  {
    icon: FiCalculator,
    title: 'E/M Code Calculator',
    description: 'Calculate Evaluation and Management coding levels based on medical decision making (MDM) and time guidelines.',
    action: 'Open Calculator',
    path: '/em-calculator',
    badge: 'Interactive Utility'
  },
  {
    icon: FiBookOpen,
    title: 'Billing & Coding Dictionary',
    description: 'Search and review essential medical terminology, abbreviations, and revenue cycle definitions.',
    action: 'Browse Terms',
    path: '/term-dictionary',
    badge: 'Reference Tool'
  },
  {
    icon: FiTag,
    title: 'Modifier Quick Tool',
    description: 'Master common pricing, informational, and anatomical modifiers to prevent claim denials.',
    action: 'Explore Modifiers',
    path: '/modifier-tool',
    badge: 'Interactive Utility'
  },
  {
    icon: FiFileText,
    title: 'ICD-10 Practice Quiz',
    description: 'Test your diagnostic coding skills with targeted scenario-based questions.',
    action: 'Start Practice Quiz',
    path: '/icd10-quiz',
    badge: 'Practice Mode'
  }
];

function ProgressDashboard({domains,completedLessons,onToggleLesson,onClose}) {
  const navigate=useNavigate();
  const lessons=useMemo(
    ()=> domains?.flatMap((domain)=> domain?.lessons || []) || [],
    [domains]
  );
  const completedCount=completedLessons?.length || 0;
  const percentage=lessons.length
    ? Math.round((completedCount / lessons.length) * 100)
    : 0;
  const nextLesson=lessons.find((lesson)=> !completedLessons?.includes(lesson.id));

  const getModuleProgress=(domain)=> {
    const moduleLessons=domain?.lessons || [];
    const complete=moduleLessons.filter((lesson)=> (
      completedLessons?.includes(lesson.id)
    )).length;

    return {
      complete,
      total: moduleLessons.length,
      isComplete: moduleLessons.length > 0 && complete === moduleLessons.length
    };
  };

  const resumeLesson=()=> {
    const domain=domains?.find((item)=> (
      item?.lessons?.some((lesson)=> lesson.id === nextLesson?.id)
    ));

    if (domain && nextLesson) {
      onClose();
      navigate(`/lesson/${domain.id}/${nextLesson.id}`);
    }
  };

  const toggleModule=(domain)=> {
    const progress=getModuleProgress(domain);

    (domain?.lessons || []).forEach((lesson)=> {
      const shouldComplete=!progress.isComplete;
      const isComplete=completedLessons?.includes(lesson.id);

      if (shouldComplete !== isComplete) {
        onToggleLesson(lesson.id);
      }
    });
  };

  const openLesson=(domain,lesson,tab)=> {
    onClose();
    navigate(`/lesson/${domain.id}/${lesson.id}`,{state: {tab}});
  };

  const openPracticeTool=(path)=> {
    onClose();
    navigate(path);
  };

  return (
    <main style={{padding: '48px 0 88px',background: '#ffffff'}}>
      <div style={{width: 'min(100% - 40px, 980px)',margin: '0 auto'}}>
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '14px'
        }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '9px 13px',
              color: '#475569',
              fontSize: '0.8rem',
              fontWeight: 700,
              background: '#ffffff',
              border: '1px solid #cbd5e1',
              borderRadius: '7px',
              cursor: 'pointer'
            }}
          >
            Close Dashboard / Return to Lesson
          </button>
        </div>

        <section style={{
          padding: '20px',
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '8px'
        }}>
          <h1 style={{
            color: '#082f49',
            fontSize: 'clamp(1.7rem,4vw,2.5rem)',
            marginBottom: '18px'
          }}>
            Curriculum Progress &amp; Study Tracker
          </h1>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '12px',
            marginBottom: '8px',
            color: '#475569',
            fontWeight: 700
          }}>
            <span>Overall completion</span>
            <strong style={{color: '#0284c7'}}>{percentage}%</strong>
          </div>

          <div style={{
            height: '10px',
            overflow: 'hidden',
            background: '#e2e8f0',
            borderRadius: '999px'
          }}>
            <span style={{
              display: 'block',
              width: `${percentage}%`,
              height: '100%',
              background: '#0284c7',
              borderRadius: 'inherit',
              transition: 'width 0.25s ease'
            }} />
          </div>

          <p style={{
            margin: '12px 0 18px',
            color: '#64748b',
            fontSize: '0.9rem'
          }}>
            {completedCount} of {lessons.length} lessons completed.
          </p>

          <button
            type="button"
            onClick={resumeLesson}
            disabled={!nextLesson}
            style={{
              padding: '11px 16px',
              color: '#ffffff',
              fontWeight: 700,
              background: nextLesson ? '#0284c7' : '#94a3b8',
              border: 0,
              borderRadius: '7px',
              cursor: nextLesson ? 'pointer' : 'not-allowed'
            }}
          >
            {nextLesson ? 'Resume Next Lesson' : 'Curriculum Complete'}
          </button>
        </section>

        <section style={{marginTop: '28px'}}>
          <h2 style={{
            marginBottom: '15px',
            color: '#082f49',
            fontSize: '1.5rem'
          }}>
            Curriculum checklist
          </h2>

          <div style={{display: 'grid',gap: '12px'}}>
            {domains?.map((domain)=> {
              const progress=getModuleProgress(domain);
              const firstLesson=domain?.lessons?.[0];

              return (
                <article
                  key={domain?.id}
                  style={{
                    padding: '17px',
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr auto',
                    gap: '14px',
                    alignItems: 'center',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    background: progress.isComplete ? '#f0fdfa' : '#ffffff'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={progress.isComplete}
                    onChange={()=> toggleModule(domain)}
                    aria-label={`Mark ${domain?.title || 'module'} complete`}
                    style={{
                      width: '18px',
                      height: '18px',
                      accentColor: '#0284c7',
                      cursor: 'pointer'
                    }}
                  />

                  <div>
                    <h3 style={{
                      color: '#0f2940',
                      fontSize: '1rem',
                      marginBottom: '5px'
                    }}>
                      {domain?.title}
                    </h3>

                    <p style={{
                      color: '#64748b',
                      fontSize: '0.84rem',
                      lineHeight: 1.5
                    }}>
                      {domain?.description}
                    </p>

                    <span style={{
                      display: 'block',
                      marginTop: '7px',
                      color: '#0284c7',
                      fontSize: '0.75rem',
                      fontWeight: 700
                    }}>
                      {progress.complete} of {progress.total} lessons complete
                    </span>
                  </div>

                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                    gap: '7px'
                  }}>
                    <button
                      type="button"
                      onClick={()=> domain?.id && navigate(`/module/${domain.id}`)}
                      style={{
                        padding: '9px 11px',
                        color: '#ffffff',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        background: '#0284c7',
                        border: 0,
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      Study Module
                    </button>

                    {firstLesson && (
                      <>
                        <button
                          type="button"
                          onClick={()=> openLesson(domain,firstLesson,'flashcards')}
                          style={{
                            padding: '8px 9px',
                            color: '#0284c7',
                            fontSize: '0.73rem',
                            fontWeight: 700,
                            background: '#eff6ff',
                            border: '1px solid #bae6fd',
                            borderRadius: '6px',
                            cursor: 'pointer'
                          }}
                        >
                          Flashcards
                        </button>

                        <button
                          type="button"
                          onClick={()=> openLesson(domain,firstLesson,'quiz')}
                          style={{
                            padding: '8px 9px',
                            color: '#0284c7',
                            fontSize: '0.73rem',
                            fontWeight: 700,
                            background: '#eff6ff',
                            border: '1px solid #bae6fd',
                            borderRadius: '6px',
                            cursor: 'pointer'
                          }}
                        >
                          Quiz
                        </button>
                      </>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section style={{marginTop: '36px'}}>
          <div style={{marginBottom: '18px'}}>
            <h2 style={{
              marginBottom: '7px',
              color: '#082f49',
              fontSize: '1.5rem'
            }}>
              Interactive Tools &amp; Practice Utilities
            </h2>
            <p style={{
              color: '#64748b',
              fontSize: '0.9rem',
              lineHeight: 1.6
            }}>
              Use these interactive utilities to reinforce your coding accuracy and speed.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(250px,1fr))',
            gap: '14px'
          }}>
            {practiceTools.map((tool)=> (
              <article
                key={tool.path}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '20px',
                  background: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  minHeight: '255px'
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '10px',
                  marginBottom: '15px'
                }}>
                  <span style={{
                    width: '42px',
                    height: '42px',
                    display: 'grid',
                    placeItems: 'center',
                    color: '#0369a1',
                    background: '#f0f9ff',
                    borderRadius: '8px'
                  }}>
                    <SafeIcon icon={tool.icon} />
                  </span>

                  <span style={{
                    padding: '5px 8px',
                    color: '#0369a1',
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    background: '#f0f9ff',
                    borderRadius: '999px'
                  }}>
                    {tool.badge}
                  </span>
                </div>

                <h3 style={{
                  marginBottom: '9px',
                  color: '#0f2940',
                  fontSize: '1.05rem'
                }}>
                  {tool.title}
                </h3>

                <p style={{
                  marginBottom: '20px',
                  color: '#64748b',
                  fontSize: '0.84rem',
                  lineHeight: 1.6
                }}>
                  {tool.description}
                </p>

                <button
                  type="button"
                  onClick={()=> openPracticeTool(tool.path)}
                  style={{
                    marginTop: 'auto',
                    alignSelf: 'flex-start',
                    padding: '8px 16px',
                    color: '#ffffff',
                    fontWeight: 600,
                    background: '#0284c7',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  {tool.action}
                </button>
              </article>
            ))}
          </div>
        </section>
      </div>
    </main>
  );
}

export default ProgressDashboard;